/**
 * The farm benchmark card on the farm dashboard: this farm against the other farms, per metric.
 *
 * Mirrors `GET /dashboard/benchmark`. Hand-written rather than taken from the generated client, for the same
 * reason `weight-milestones.ts` is - see `WeightMilestoneService`. Field names match the API exactly so the
 * two stay easy to diff.
 *
 * "Others" never includes the farm itself, and every other farm counts once whatever its size. The response
 * carries no farm names and no position among the farms, and the card must not derive one.
 */

/** A `FarmBenchmarkMetric` id. Sent in lifecycle order; render them in the order received. */
export type FarmBenchmarkMetricId =
  | 'BirthWeight'
  | 'WeanedWeight'
  | 'NurseryGrowth'
  | 'FatteningGrowth'
  | 'Mortality'
  | 'SlaughterWeight';

/** The figures shared by the twelve-month total and each of its weeks. Every value is rounded to 1 decimal. */
export interface FarmBenchmarkFigures {
  /** Null when the farm has no data for it - never zero for "nothing". */
  farmValue: number | null;
  /** Animals behind `farmValue`; for mortality, the births it is a share of. */
  farmSampleSize: number;
  /** Average of the other farms, one vote per farm. Null when no other farm had data. */
  othersAverage: number | null;
  othersMin: number | null;
  othersMax: number | null;
  otherFarmsCount: number;
}

export interface FarmBenchmarkWeek extends FarmBenchmarkFigures {
  /** First day of the week. */
  weekStartDate: string;
}

export interface FarmBenchmarkMetric extends FarmBenchmarkFigures {
  /** Typed as a string, not `FarmBenchmarkMetricId`: a metric added on the backend must still render. */
  metric: string;
  /** "g", "kg", "g/day" or "%". */
  unit: string;
  /** True for mortality. Decides which side of the average is good - never infer it from the metric. */
  lowerIsBetter: boolean;
  /** 52 entries, oldest first, empty weeks included. */
  weeks: FarmBenchmarkWeek[];
}

export interface FarmBenchmark {
  /** First day of the period. */
  periodFrom: string | null;
  /** Last day of the period, included. */
  periodTo: string | null;
  /** Null when the benchmark was never calculated; `metrics` is then empty. */
  calculatedAt: string | null;
  metrics: FarmBenchmarkMetric[];
}

/**
 * `calculatedAt` is UTC but arrives without an offset (a `timestamp without time zone` column), and
 * `new Date()` reads an offset-less string as browser-local time. Marking it UTC keeps the shown time
 * right for a reader outside the farm's time zone.
 */
export const benchmarkTimestamp = (value: string): string =>
  /(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`;

/**
 * `periodFrom`, `periodTo` and `weekStartDate` are calendar days, not instants. Noon UTC formats to the
 * same day in every farm time zone, where midnight would roll back a day west of Greenwich.
 */
export const benchmarkDay = (value: string): string => `${value.slice(0, 10)}T12:00:00Z`;
